import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  type User as FirebaseUser,
  type ConfirmationResult,
  type ApplicationVerifier
} from 'firebase/auth'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { auth, db, storage, googleProvider, facebookProvider, signInWithPhoneNumber } from '../lib/firebase'
import type { User } from '../types'

// Import other stores for cleanup on logout - prevents cross-user data leakage
import { useCartStore } from './cartStore'
import { useLoyaltyStore } from './loyaltyStore'
import { useRaffleStore } from './raffleStore'

// Current versions for terms and privacy
const CURRENT_TERMS_VERSION = '1.0'
const CURRENT_PRIVACY_VERSION = '1.0'

// Helper to check if error has Firebase auth code
const isFirebaseError = (error: unknown): error is { code: string; message: string } => {
  return typeof error === 'object' && error !== null && 'code' in error && 'message' in error
}

interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  error: string | null
  isInitialized: boolean
  isAuthModalOpen: boolean
  phoneConfirmationResult: ConfirmationResult | null
  needsTermsAcceptance: boolean
  isTermsModalOpen: boolean
  needsOnboarding: boolean
  isOnboardingModalOpen: boolean
}

interface AuthActions {
  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  signInWithGoogle: () => Promise<void>
  signInWithFacebook: () => Promise<void>
  signInWithPhone: (phoneNumber: string, recaptchaVerifier: ApplicationVerifier) => Promise<void>
  verifyPhoneCode: (code: string) => Promise<void>
  signOut: () => Promise<void>
  initializeAuth: () => void
  clearError: () => void
  createOrUpdateUser: (firebaseUser: FirebaseUser) => Promise<void>
  checkAdminRole: (userId: string) => Promise<boolean>
  updateUserAddress: (addressData: {
    firstName?: string
    lastName?: string
    addressLine1: string
    number: string
    addressLine2: string
    city: string
    state: string
    postalCode: string
    country: string
    phone: string
  }) => Promise<void>
  uploadProfilePhoto: (file: File) => Promise<string>
  updateProfilePhoto: (photoURL: string) => Promise<void>
  openAuthModal: () => void
  closeAuthModal: () => void
  updateUserTermsAcceptance: (termsData: {
    termsAcceptedAt: Date
    termsVersion: string
    privacyAcceptedAt: Date
    privacyVersion: string
  }) => Promise<void>
  checkTermsAcceptance: (user: User) => boolean
  openTermsModal: () => void
  closeTermsModal: () => void
  checkOnboardingNeeded: (user: User) => boolean
  openOnboardingModal: () => void
  closeOnboardingModal: () => void
}

export type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set, get) => ({
      // State
      user: null,
      isLoading: false,
      isAuthenticated: false,
      isAdmin: false,
      error: null,
      isInitialized: false,
      isAuthModalOpen: false,
      phoneConfirmationResult: null,
      needsTermsAcceptance: false,
      isTermsModalOpen: false,
      needsOnboarding: false,
      isOnboardingModalOpen: false,

      // Actions
      setUser: (user) => {
        const needsOnboarding = user ? get().checkOnboardingNeeded(user) : false

        set({
          user,
          isAuthenticated: !!user,
          isAdmin: user?.isAdmin || user?.role === 'admin' || user?.role === 'super_admin' || false,
          isLoading: false,
          needsOnboarding,
          isOnboardingModalOpen: needsOnboarding,
          // Legacy support - keep terms modal closed if onboarding handles it
          needsTermsAcceptance: false,
          isTermsModalOpen: false
        })
      },

      setLoading: (isLoading) => {
        set({ isLoading })
      },

      setError: (error) => {
        set({ error, isLoading: false })
      },

      clearError: () => {
        set({ error: null })
      },

      checkAdminRole: async () => {
        try {
          // For now, return false for all users to avoid permission errors
          // In the future, you can implement proper admin checking via:
          // 1. Firebase Functions with admin SDK
          // 2. Custom claims in Firebase Auth
          // 3. A field in the user document itself
          return false
        } catch (error) {
          console.error('Error checking admin role:', error)
          return false
        }
      },

      createOrUpdateUser: async (firebaseUser: FirebaseUser) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
          if (userDoc.exists()) {
            // Update existing user - preserve existing role and admin status
            const userData = userDoc.data() as User
            const updatedUser: User = {
              ...userData,
              lastLoginAt: new Date(),
              displayName: firebaseUser.displayName || userData.displayName,
              photoURL: firebaseUser.photoURL || userData.photoURL,
              emailVerified: firebaseUser.emailVerified,
              // Preserve existing admin status and role
              isAdmin: userData.isAdmin || false,
              role: userData.role || 'user',
              // Ensure shippingAddresses is always an array
              shippingAddresses: userData.shippingAddresses || []
            }
            await setDoc(doc(db, 'users', firebaseUser.uid), updatedUser, { merge: true })
            get().setUser(updatedUser)
          } else {
            // Create new user - new users start as regular users, not admins
            const newUser: User = {
              id: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || '',
              photoURL: firebaseUser.photoURL || '',
              phoneNumber: firebaseUser.phoneNumber || '',
              emailVerified: firebaseUser.emailVerified,
              isAdmin: false,
              role: 'user',
              createdAt: new Date(),
              lastLoginAt: new Date(),

              // Profile completion
              profileComplete: false,

              // Personal information (from OAuth if available)
              firstName: firebaseUser.displayName?.split(' ')[0] || '',
              lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',

              // Shipping addresses
              shippingAddresses: [],

              preferences: {
                notifications: {
                  email: true,
                  push: true,
                  sms: false
                },
                language: 'es'
              },
              stats: {
                totalSpent: 0,
                totalTickets: 0,
                totalWins: 0,
                totalWinnings: 0
              }
            }
            await setDoc(doc(db, 'users', firebaseUser.uid), newUser)
            get().setUser(newUser)
          }
        } catch (error) {
          console.error('Error creating/updating user:', error)
          throw error
        }
      },



      signOut: async () => {
        try {
          set({ isLoading: true, error: null })

          // SECURITY FIX: Clear all user-specific stores BEFORE Firebase signout
          // This prevents cross-user data leakage on shared devices
          try {
            useCartStore.getState().clearCart()
            useLoyaltyStore.getState().reset()
            useRaffleStore.getState().cleanup()
          } catch (cleanupError) {
            console.warn('Error cleaning up stores during signout:', cleanupError)
            // Continue with signout even if cleanup fails
          }

          await firebaseSignOut(auth)
          set({
            user: null,
            isAuthenticated: false,
            isAdmin: false,
            isLoading: false,
            needsOnboarding: false,
            isOnboardingModalOpen: false,
            needsTermsAcceptance: false,
            isTermsModalOpen: false,
            phoneConfirmationResult: null
          })
        } catch (error: unknown) {
          console.error('Sign out error:', error)
          set({
            error: 'Error al cerrar sesión',
            isLoading: false
          })
        }
      },

      signInWithGoogle: async () => {
        try {
          set({ isLoading: true, error: null })

          const credential = await signInWithPopup(auth, googleProvider)
          await get().createOrUpdateUser(credential.user)
        } catch (error: unknown) {
          console.error('Google sign in error:', error)
          set({
            error: isFirebaseError(error) && error.code === 'auth/cancelled-popup-request'
              ? 'Inicio de sesión cancelado'
              : isFirebaseError(error) && error.code === 'auth/popup-blocked'
              ? 'Pop-up bloqueado. Permite pop-ups para continuar'
              : isFirebaseError(error) && error.code === 'auth/popup-closed-by-user'
              ? 'Pop-up cerrado por el usuario'
              : 'Error al iniciar sesión con Google',
            isLoading: false
          })
        }
      },

      signInWithFacebook: async () => {
        try {
          set({ isLoading: true, error: null })

          const credential = await signInWithPopup(auth, facebookProvider)
          await get().createOrUpdateUser(credential.user)
        } catch (error: unknown) {
          console.error('Facebook sign in error:', error)
          set({
            error: isFirebaseError(error) && error.code === 'auth/cancelled-popup-request'
              ? 'Inicio de sesión cancelado'
              : isFirebaseError(error) && error.code === 'auth/popup-blocked'
              ? 'Pop-up bloqueado. Permite pop-ups para continuar'
              : 'Error al iniciar sesión con Facebook',
            isLoading: false
          })
        }
      },

      signInWithPhone: async (phoneNumber: string, recaptchaVerifier: ApplicationVerifier) => {
        set({ isLoading: true, error: null })

        try {
          if (!recaptchaVerifier) {
            console.error('reCAPTCHA verifier is missing - cannot proceed with phone authentication')
            set({ error: 'Error de configuración. Por favor recarga la página.' })
            return
          }

          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('SMS timeout - Firebase no responde')), 15000)
          })

          const confirmationResult = await Promise.race([
            signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier),
            timeoutPromise
          ]) as any

          set({ phoneConfirmationResult: confirmationResult })
        } catch (error: unknown) {
          console.error('Phone authentication error:', error)

          // Detailed error logging for debugging
          if (isFirebaseError(error)) {
            console.error('Firebase auth error:', error.code, error.message)

            // Log critical errors that need immediate attention
            if (error.code === 'auth/quota-exceeded') {
              console.error('CRITICAL: SMS quota exceeded - check Firebase billing and usage')
            } else if (error.code === 'auth/too-many-requests') {
              console.error('CRITICAL: Too many requests blocked by Firebase - possible abuse')
            } else if (error.code === 'auth/app-not-authorized') {
              console.error('CRITICAL: App not authorized - check Firebase Console authorized domains')
            } else if (error.code === 'auth/invalid-app-credential') {
              console.error('CRITICAL: Invalid app credentials - check Firebase configuration')
            } else if ((error as any)?.message?.includes('TOO_MANY_ATTEMPTS_TRY_LATER')) {
              console.error('CRITICAL: Firebase SMS rate limit exceeded - wait 15-30 minutes before retry')
            }
          }

          set({
            error: isFirebaseError(error) && error.code === 'auth/too-many-requests'
              ? 'Demasiados intentos. Inténtalo más tarde'
              : isFirebaseError(error) && error.code === 'auth/invalid-phone-number'
              ? 'Número de teléfono inválido'
              : isFirebaseError(error) && error.code === 'auth/quota-exceeded'
              ? 'Límite de SMS alcanzado. Inténtalo más tarde'
              : isFirebaseError(error) && error.code === 'auth/invalid-app-credential'
              ? 'Error de configuración de Firebase. Contacta al administrador'
              : isFirebaseError(error) && error.code === 'auth/app-not-authorized'
              ? 'Aplicación no autorizada. Contacta al administrador'
              : (error as any)?.message?.includes('TOO_MANY_ATTEMPTS_TRY_LATER')
              ? 'Demasiados intentos de SMS. Espera 15-30 minutos antes de intentar de nuevo'
              : 'Error al enviar SMS. Verifica el número',
            phoneConfirmationResult: null
          })
          // Don't re-throw to prevent hanging state
        } finally {
          // ALWAYS reset loading state, regardless of success or failure
          set({ isLoading: false })
        }
      },

      verifyPhoneCode: async (code: string) => {
        const confirmationResult = get().phoneConfirmationResult
        if (!confirmationResult) {
          set({ error: 'No hay confirmación de SMS pendiente' })
          return
        }

        try {
          set({ isLoading: true, error: null })

          const credential = await confirmationResult.confirm(code)
          await get().createOrUpdateUser(credential.user)

          // Clear confirmation result after successful verification
          set({ phoneConfirmationResult: null })
        } catch (error: unknown) {
          console.error('Phone code verification error:', error)
          set({
            error: isFirebaseError(error) && error.code === 'auth/invalid-verification-code'
              ? 'Código de verificación incorrecto'
              : isFirebaseError(error) && error.code === 'auth/code-expired'
              ? 'El código ha expirado. Solicita uno nuevo'
              : 'Error al verificar el código',
            isLoading: false
          })
          throw error
        }
      },



      initializeAuth: () => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
          if (firebaseUser) {
            try {
              const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
              if (userDoc.exists()) {
                const userData = userDoc.data() as User
                // Ensure shippingAddresses is always an array
                const userWithDefaults = {
                  ...userData,
                  shippingAddresses: userData.shippingAddresses || []
                }
                get().setUser(userWithDefaults)
              } else {
                // Create user document if it doesn't exist
                const newUser: User = {
                  id: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName: firebaseUser.displayName || '',
                  photoURL: firebaseUser.photoURL || '',
                  phoneNumber: firebaseUser.phoneNumber || '',
                  emailVerified: firebaseUser.emailVerified,
                  createdAt: new Date(),
                  lastLoginAt: new Date(),

                  // Profile completion
                  profileComplete: false,
                  canPurchaseTickets: false,
                  contactMethodVerified: firebaseUser.phoneNumber ? 'phone' : (firebaseUser.email ? 'email' : 'none'),

                  // Personal information
                  firstName: firebaseUser.displayName?.split(' ')[0] || '',
                  lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',

                  // Shipping addresses
                  shippingAddresses: [],

                  preferences: {
                    notifications: {
                      email: true,
                      push: true,
                      sms: false
                    },
                    language: 'es'
                  },
                  stats: {
                    totalSpent: 0,
                    totalTickets: 0,
                    totalWins: 0,
                    totalWinnings: 0
                  },
                  loyalty: {
                    totalPoints: 0,
                    currentLevel: null,
                    pointsToNextLevel: 500,
                    progressPercentage: 0,
                    hasExplorerBadge: false,
                    hasHunterBadge: false,
                    hasChampionBadge: false,
                    hasDiamondBadge: false
                  }
                }
                await setDoc(doc(db, 'users', firebaseUser.uid), newUser)
                get().setUser(newUser)
              }
            } catch (error) {
              console.error('Error fetching user data:', error)
              get().setUser(null)
            }
          } else {
            get().setUser(null)
          }
          
          set({ isInitialized: true })
        })

        // Return unsubscribe function for cleanup
        return unsubscribe
      },

      updateUserAddress: async (addressData) => {
        const currentUser = get().user
        if (!currentUser || !auth.currentUser) {
          throw new Error('No user authenticated')
        }

        try {
          set({ isLoading: true, error: null })

          // Update user data
          const updatedUser: User = {
            ...currentUser,
            firstName: addressData.firstName || currentUser.firstName,
            lastName: addressData.lastName || currentUser.lastName,
            defaultShippingAddress: {
              firstName: addressData.firstName || currentUser.firstName || '',
              lastName: addressData.lastName || currentUser.lastName || '',
              addressLine1: addressData.addressLine1,
              number: addressData.number,
              addressLine2: addressData.addressLine2,
              city: addressData.city,
              state: addressData.state,
              postalCode: addressData.postalCode,
              country: addressData.country,
              phone: addressData.phone,
              email: currentUser.email || '',
              isDefault: true
            },
            profileComplete: true // Mark profile as complete after address update
          }

          // Save to Firestore
          await setDoc(doc(db, 'users', auth.currentUser.uid), updatedUser, { merge: true })

          // Update local state
          set({ user: updatedUser })

        } catch (error) {
          console.error('Error updating user address:', error)
          if (isFirebaseError(error)) {
            set({ error: error.message })
          } else {
            set({ error: 'Error al actualizar la dirección' })
          }
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      uploadProfilePhoto: async (file: File) => {
        const currentUser = get().user
        if (!currentUser || !auth.currentUser) {
          throw new Error('No user authenticated')
        }

        try {
          set({ isLoading: true, error: null })

          // Delete existing photo if it exists and is a storage URL
          if (currentUser.photoURL && currentUser.photoURL.includes('firebase')) {
            try {
              const oldRef = ref(storage, currentUser.photoURL)
              await deleteObject(oldRef)
            } catch (error) {
              console.warn('Could not delete old photo:', error)
            }
          }

          // Create unique filename
          const fileExtension = file.name.split('.').pop()
          const fileName = `profile-photos/${currentUser.id}-${Date.now()}.${fileExtension}`
          const storageRef = ref(storage, fileName)

          // Upload file
          const snapshot = await uploadBytes(storageRef, file)
          const downloadURL = await getDownloadURL(snapshot.ref)

          return downloadURL
        } catch (error) {
          console.error('Error uploading profile photo:', error)
          if (isFirebaseError(error)) {
            set({ error: error.message })
          } else {
            set({ error: 'Error al subir la foto de perfil' })
          }
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      updateProfilePhoto: async (photoURL: string) => {
        const currentUser = get().user
        if (!currentUser || !auth.currentUser) {
          throw new Error('No user authenticated')
        }

        try {
          set({ isLoading: true, error: null })

          // Update Firebase Auth profile
          await updateProfile(auth.currentUser, { photoURL })

          // Update user document in Firestore
          const updatedUser: User = {
            ...currentUser,
            photoURL
          }

          await setDoc(doc(db, 'users', auth.currentUser.uid), updatedUser, { merge: true })

          // Update local state
          set({ user: updatedUser })

        } catch (error) {
          console.error('Error updating profile photo:', error)
          if (isFirebaseError(error)) {
            set({ error: error.message })
          } else {
            set({ error: 'Error al actualizar la foto de perfil' })
          }
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      openAuthModal: () => {
        set({ isAuthModalOpen: true })
      },

      closeAuthModal: () => {
        set({ isAuthModalOpen: false })
      },

      checkTermsAcceptance: (user: User) => {
        if (!user.termsAcceptedAt || !user.privacyAcceptedAt) {
          return false
        }

        // Check if user accepted current versions
        const hasCurrentTerms = user.termsVersion === CURRENT_TERMS_VERSION
        const hasCurrentPrivacy = user.privacyVersion === CURRENT_PRIVACY_VERSION

        return hasCurrentTerms && hasCurrentPrivacy
      },

      updateUserTermsAcceptance: async (termsData) => {
        const currentUser = get().user
        if (!currentUser || !auth.currentUser) {
          throw new Error('No user authenticated')
        }

        try {
          set({ isLoading: true, error: null })

          // Update user document in Firestore
          const updatedUser: User = {
            ...currentUser,
            ...termsData
          }

          await setDoc(doc(db, 'users', auth.currentUser.uid), updatedUser, { merge: true })

          // Update local state
          set({
            user: updatedUser,
            needsTermsAcceptance: false,
            isTermsModalOpen: false
          })

        } catch (error) {
          console.error('Error updating terms acceptance:', error)
          if (isFirebaseError(error)) {
            set({ error: error.message })
          } else {
            set({ error: 'Error al actualizar términos y condiciones' })
          }
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      openTermsModal: () => {
        set({ isTermsModalOpen: true })
      },

      closeTermsModal: () => {
        set({ isTermsModalOpen: false })
      },

      checkOnboardingNeeded: (user: User) => {
        // Check only the three required fields for onboarding completion
        return !user.hasAcceptedTerms || !user.profileComplete || !user.hasCompletedOnboarding
      },

      openOnboardingModal: () => {
        set({ isOnboardingModalOpen: true })
      },

      closeOnboardingModal: () => {
        set({
          isOnboardingModalOpen: false,
          needsOnboarding: false
        })
      }
    }),
    {
      name: 'auth-store'
    }
  )
)